import { watch } from "vue";
import { useRoute, useRouter, type LocationQuery } from "vue-router";

type RawParamValue = LocationQuery[string] | undefined;

export interface UrlParamCodec<T> {
  encode(value: T | undefined): string | string[] | null;
  decode(raw: RawParamValue): T;
}

export type UrlParamsSchema = Record<string, UrlParamCodec<unknown>>;

export type UrlFiltersState<P extends UrlParamsSchema> = {
  [K in keyof P]: P[K] extends UrlParamCodec<infer T> ? T : never;
};

const firstValue = (raw: RawParamValue): string | null => {
  const value = Array.isArray(raw) ? raw[0] : raw;
  return typeof value === "string" && value ? value : null;
};

const allValues = (raw: RawParamValue): string[] => {
  if (raw == null) return [];
  return (Array.isArray(raw) ? raw : [raw]).filter((value): value is string => typeof value === "string" && value !== "");
};

export const stringParam = (): UrlParamCodec<string> => ({
  encode: (value) => value || null,
  decode: (raw) => firstValue(raw) || "",
});

export const arrayParam = <T = string>(cast: (raw: string) => T = (raw) => raw as T): UrlParamCodec<T[]> => ({
  encode: (values) => (values?.length ? values.map(String) : null),
  decode: (raw) => allValues(raw).map(cast).filter((value) => !Number.isNaN(value)),
});

export const flagParam = (): UrlParamCodec<boolean> => ({
  encode: (value) => (value ? "1" : null),
  decode: (raw) => firstValue(raw) === "1",
});

// A bounded value whose default is a number rather than an empty state, e.g.
// the ends of an age slider: at the default the param disappears.
export const numberParam = (fallback: number): UrlParamCodec<number> => ({
  encode: (value) => (value == null || value === fallback ? null : String(value)),
  decode: (raw) => {
    const value = firstValue(raw);
    if (value === null) return fallback;

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  },
});

export const pageParam = (): UrlParamCodec<number> => ({
  encode: (value) => (value && value > 1 ? String(value) : null),
  decode: (raw) => Math.max(parseInt(firstValue(raw) || "", 10) || 1, 1),
});

export const hasUrlFilterParams = (query: LocationQuery, params: UrlParamsSchema): boolean =>
  Object.keys(params).some((key) => query[key] != null);

interface UseFiltersUrlSyncOptions<P extends UrlParamsSchema> {
  params: P;
  serialize: () => Partial<UrlFiltersState<P>>;
  apply: (state: UrlFiltersState<P>) => void;
  onExternalChange?: (state: UrlFiltersState<P>) => void;
}

export const useFiltersUrlSync = <P extends UrlParamsSchema>({
  params,
  serialize,
  apply,
  onExternalChange,
}: UseFiltersUrlSyncOptions<P>) => {
  const route = useRoute();
  const router = useRouter();
  const routeName = route.name;
  const keys = Object.keys(params) as (keyof P & string)[];

  const encodeState = (state: Partial<UrlFiltersState<P>>) => {
    const encoded: Record<string, string | string[]> = {};
    for (const key of keys) {
      const value = params[key].encode(state[key]);
      if (value !== null) encoded[key] = value;
    }
    return encoded;
  };

  const decodeQuery = (query: LocationQuery) => {
    const state = {} as UrlFiltersState<P>;
    for (const key of keys) state[key] = params[key].decode(query[key]) as UrlFiltersState<P>[typeof key];
    return state;
  };

  const canonical = (query: Record<string, RawParamValue>) => JSON.stringify(keys.map((key) => [key, allValues(query[key])]));

  const syncUrl = () => {
    if (route.name !== routeName) return;
    const encoded = encodeState(serialize());
    if (canonical(route.query) === canonical(encoded)) return;
    const query = { ...route.query };
    for (const key of keys) delete query[key];
    router.replace({ query: { ...query, ...encoded } });
  };

  const restoreFromUrl = (): boolean => {
    if (!hasUrlFilterParams(route.query, params)) return false;
    apply(decodeQuery(route.query));
    return true;
  };

  if (onExternalChange) {
    watch(
      () => route.query,
      (query) => {
        if (route.name !== routeName) return;
        if (canonical(query) === canonical(encodeState(serialize()))) return;
        onExternalChange(decodeQuery(query));
      },
    );
  }

  return { syncUrl, restoreFromUrl };
};
